import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  Alert,
  InputAdornment,
  TextField,
} from "@mui/material";
import { CirclePlus, Search } from "lucide-react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmationModal from "../../components/shared/modals/DeleteConfirmationModal";
import {
  getServiceLevel,
  createServiceLevel,
  updateServiceLevel,
  deleteServiceLevel,
} from "../../api/Service Level/api";
import usePermissions from "./hooks/usePermissions";
import DataTable from "../../components/DataTable";
import ServiceLevelForm from "../../components/shared/forms/ServiceLevelForm";
import { AuthContext } from "../../contexts/AuthContext";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 1,
  p: 0,
};

const ServiceLevel = () => {
  const {
    loading: loadingPermissions,
    error: permissionsError,
    can,
  } = usePermissions();
  const { user } = useContext(AuthContext);
  const [serviceLevel, setServiceLevel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedServiceLevel, setSelectedServiceLevel] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [serviceLevelToDelete, setServiceLevelToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (!loadingPermissions && can.viewServiceLevel()) {
      fetchServiceLevel();
    }
  }, [loadingPermissions]);

  const fetchServiceLevel = async () => {
    try {
      setLoading(true);
      const response = await getServiceLevel();

      if (response?.data) {
        const transformedData = response.data.map((serviceLevel) => {
          return {
            id: serviceLevel.id,
            duka: serviceLevel.duka,
            product: serviceLevel.product,
            date: serviceLevel.date.split("T")[0],
            ordersFulfilled: serviceLevel.orders_fulfilled,
            ordersPlaced: serviceLevel.orders_placed,
            serviceLevelPercentage: serviceLevel.service_level_percentage,
            // Keep original data for form population
            original: serviceLevel,
          };
        });
        console.log("Service Level loaded:", transformedData);
        setServiceLevel(transformedData);
      }

      setError(null);
    } catch (err) {
      console.error("Error fetching Service Level:", err);
      setError("Failed to load Service Level. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const transformServiceLevelData = (data) => {
    return {
      duka: data.duka,
      product: data.product,
      date: data.date,
      orders_fulfilled: parseInt(data.orders_fulfilled),
      orders_placed: parseInt(data.orders_placed),
      service_level_percentage: parseFloat(data.service_level_percentage),
    };
  };

  const handleCreateServiceLevel = async (data) => {
    try {
      const transformedData = transformServiceLevelData(data);

      await createServiceLevel(transformedData);
      setOpenModal(false);
      fetchServiceLevel();
    } catch (error) {
      console.error("Error creating Service Level:", error);
      setError(
        error.response?.data?.message ||
          "Failed to create Service Level. Please try again."
      );
    }
  };

  const handleUpdateServiceLevel = async (id, data) => {
    try {
      const transformedData = transformServiceLevelData(data);
      await updateServiceLevel(id, transformedData);
      setOpenModal(false);
      setSelectedServiceLevel(null);
      fetchServiceLevel();
    } catch (error) {
      console.error("Error updating Service Level:", error);
      setError(
        error.response?.data?.message ||
          "Failed to update service level. Please try again."
      );
    }
  };

  const handleDeleteServiceLevel = async () => {
    if (!serviceLevelToDelete) return;

    try {
      await deleteServiceLevel(serviceLevelToDelete.id);
      setDeleteModalOpen(false);
      setServiceLevelToDelete(null);
      fetchServiceLevel();
    } catch (error) {
      console.error("Error deleting Service Level:", error);
      setError("Failed to delete Service Level. Please try again.");
    }
  };

  const handleSubmit = (data) => {
    if (selectedServiceLevel) {
      handleUpdateServiceLevel(selectedServiceLevel.id, data);
    } else {
      handleCreateServiceLevel(data);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredServiceLevel = serviceLevel.filter((serviceLevel) =>
    Object.values(serviceLevel).some(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const columns = [
    { id: "duka", label: "Duka" },
    { id: "product", label: "Product" },
    { id: "date", label: "Date" },
    { id: "ordersFulfilled", label: "Orders Fulfilled" },
    { id: "ordersPlaced", label: "Orders Placed" },
    { id: "serviceLevelPercentage", label: "Service Level Percentage" },
  ];

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{ color: "#124F3E", fontWeight: "bold", mb: 3 }}
      >
        Manage Service Level
      </Typography>

      {(error || permissionsError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || permissionsError}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <TextField
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: "300px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={20} />
              </InputAdornment>
            ),
            sx: { height: "40px" },
          }}
        />

        {can.createServiceLevel() && (
          <Button
            variant="contained"
            startIcon={<CirclePlus size={18} />}
            onClick={() => {
              setSelectedServiceLevel(null);
              setOpenModal(true);
            }}
            sx={{
              fontWeight: "bold",
              bgcolor: "#124F3E",
              "&:hover": {
                backgroundColor: "#1a614d !important",
              },
            }}
          >
            Add Service Level
          </Button>
        )}
      </Box>

      <DataTable
        loading={loading || loadingPermissions}
        columns={columns}
        rows={filteredServiceLevel}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onEdit={
          can.editServiceLevel()
            ? (rowData) => {
                setSelectedServiceLevel(rowData);
                setOpenModal(true);
              }
            : undefined
        }
        onDelete={
          can.deleteServiceLevel()
            ? (rowData) => {
                setServiceLevelToDelete(rowData);
                setDeleteModalOpen(true);
              }
            : undefined
        }
        editIcon={<EditIcon />}
        deleteIcon={<DeleteIcon />}
        editTooltip="Edit"
        deleteTooltip="Delete"
        editDisabled={!can.editServiceLevel()}
        deleteDisabled={!can.deleteServiceLevel()}
      />

      {(can.createServiceLevel() || can.editServiceLevel()) && (
        <Modal
          open={openModal}
          onClose={() => {
            setSelectedServiceLevel(null);
            setOpenModal(false);
          }}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={modalStyle}>
            <ServiceLevelForm
              initialData={selectedServiceLevel?.original || null}
              onSubmit={handleSubmit}
              onCancel={() => {
                setSelectedServiceLevel(null);
                setOpenModal(false);
              }}
            />
          </Box>
        </Modal>
      )}

      {can.deleteServiceLevel() && (
        <DeleteConfirmationModal
          open={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setServiceLevelToDelete(null);
          }}
          onConfirm={handleDeleteServiceLevel}
          title="Delete Service Level"
          message="Are you sure you want to delete this service level?"
          itemName={serviceLevelToDelete?.name}
        />
      )}
    </Box>
  );
};

export default ServiceLevel;
